import { useId, useState } from 'react'
import type { GateAnswered } from '../../stream/reducer'
import { formatWhen } from '../../time'
import type { ReactNode } from 'react'

/** What the caller sends back when the reader decides. */
export interface GateAnswer {
  decision: string

  /** The raw string, VERBATIM. Never the literal word "Other". */
  note?: string

  /** Anything a particular card collects beyond the verdict. */
  extra?: Record<string, unknown>
}

export interface GateShellProps {
  kind: string
  origin: 'harness' | 'agent'
  state: string
  options: readonly GateOption[]
  children: ReactNode
  busy?: boolean
  onAnswer: (answer: GateAnswer) => void

  /** The card's heading — one sentence saying what is being decided. */
  heading?: string

  /**
   * The decision, once a person has made one. An ANSWERED CARD IS INERT AND STAYS ON THE PAGE
   * (`AC-UI-8`): the decision is the most important thing on it, and a card that disappeared would
   * leave the reader scrolling a conversation with its decisions cut out.
   */
  answer?: GateAnswered | null

  /** The specialist that raised an agent-raised card, matching the turn heading above it. */
  from?: string | null

  /**
   * AN ACKNOWLEDGEMENT THE READER MUST TICK, and a reason they must write, before ONE named option
   * can be submitted. `AC-UI-19`: the assumption-override card keeps *Override* disabled until both
   * are supplied.
   *
   * <p>It is a property of the CARD, not of the shell's own judgement: overriding an assumption is
   * the one decision in this system where the consequence is carried entirely by the person making
   * it, and a card that let it through on a radio button alone would be collecting a click rather
   * than a decision.</p>
   */
  acknowledge?: {
    option: string
    text: string

    /**
     * WHETHER A REASON IS REQUIRED AS WELL AS THE TICK, which is the difference between two MUSTs
     * that look alike and are not.
     *
     * <p>`AC-UI-19` — assumption override — keeps *Override* disabled "until BOTH a non-empty
     * reason AND the acknowledgement checkbox are supplied", because overriding an assumption is a
     * claim about this analysis that the report will print.</p>
     *
     * <p>`§5.2` — a `not-adequate` binding — asks for the CHECKBOX ALONE: "requires a separate
     * explicit acknowledgement checkbox before Accept enables — *I acknowledge this is an unsourced
     * estimate*". There is nothing to explain: the grade already says what is wrong with the value,
     * and the card's weaknesses list says it at length.</p>
     *
     * <p>Applying the override's two-part rule to the binding card left Accept disabled with the
     * box ticked and no way to see why.</p>
     */
    requiresReason: boolean
  } | null

  /**
   * WHICH OPTION IS SELECTED, for a card whose body depends on it. <i>Supply my own…</i> opens the
   * upload control; nothing else does. The shell owns the selection, so it says so rather than the
   * card keeping a second copy that can disagree with the radio group.
   */
  onChosen?: (option: string | null) => void
}

/** One option, and WHAT IT MEANS. Every option carries its consequence. */
export interface GateOption {
  value: string
  label: string
  consequence: string
}

/**
 * The common shell of all ten cards. `04 §5.1`.
 *
 * <p>Header is `KIND · FROM · STATE`, with FROM populated only for the two agent-raised kinds —
 * under R-9 the words "raised by the harness" are removed, because a card that says who raised it
 * on every row teaches the reader to skip the row.</p>
 *
 * <p><b>A FREE-TEXT FIELD ON EVERY QUESTION, ALWAYS.</b> A person must be able to say something the
 * option set did not anticipate, and what they type is recorded verbatim. The literal word "Other"
 * is never recorded: it is not an answer, it is the absence of one.</p>
 *
 * <p><b>THE CLIENT SENDS WHICH OPTION WAS CHOSEN AND NOTHING ELSE.</b> `approved` is derived
 * server-side — there is no field here that could assert it.</p>
 */
export function GateShell(props: GateShellProps): React.JSX.Element {
  const { kind, origin, state, options, children, busy = false, onAnswer,
          heading, answer = null, from = null, acknowledge = null, onChosen } = props
  const [chosen, setChosen] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const headingId = useId()
  const noteId = useId()
  const group = useId()

  // SUBMIT IS DISABLED UNTIL A DECISION IS PICKED OR FREE TEXT IS TYPED — and free text alone
  // answers only an AGENT-RAISED card. There the specialist reads the answer verbatim and can act on
  // a sentence. At a harness-raised card nobody reads it: the option decides whether the run
  // continues, goes back or stops, and a verdict outside the card's options halts the engagement.
  // So there the note travels WITH an option, never instead of one. B-190.
  const noteAnswers = origin === 'agent'
  const gated = acknowledge !== null && chosen === acknowledge.option
  const answerable = (chosen !== null || (noteAnswers && note.trim().length > 0))
    && (!gated
        || (acknowledged
            // A REASON TOO, WHERE THE SPECIFICATION ASKS FOR ONE. For an override, both: a reason
            // with no acknowledgement is a note, an acknowledgement with no reason records that
            // somebody clicked, and the report prints the reason. For a `not-adequate` binding,
            // the tick alone — the grade and the weaknesses already say what is wrong with it.
            && (!acknowledge.requiresReason || note.trim().length > 0)))
  const open = answer === null

  return (
    <section className={`gate${open ? ' open' : ' answered'}`} role="group"
             aria-labelledby={headingId}>
      {/* KIND · FROM · STATE. `from` names the SPECIALIST that raised an agent-raised card, which
          matches the turn heading directly above it; the eight harness-raised kinds render nothing
          there, because which side of an internal boundary raised a card is not a fact an
          economist can act on (R-9). */}
      <header>
        <span className="kind" id={headingId}>{label(kind)}</span>
        {origin === 'agent' && (
          <span className="from">raised by the {from ?? 'analyst'}</span>
        )}
        <span className="state">{open ? state : answeredState(answer)}</span>
      </header>

      <div className="body">
        {heading !== undefined && <h3>{heading}</h3>}

        {children}

        {open ? (
          <>
            <fieldset className="opts">
              <legend className="mlabel" style={{ margin: '0 0 8px' }}>Your decision</legend>

              {options.map((option) => (
                <label className="opt" key={option.value}>
                  <input type="radio" name={group} value={option.value}
                         checked={chosen === option.value}
                         onChange={() => { setChosen(option.value); onChosen?.(option.value) }} />
                  <span>
                    <b>{option.label}</b>
                    <span className="d">{option.consequence}</span>
                  </span>
                </label>
              ))}

              {/* `§5.1`: "a free-text field on EVERY question, ALWAYS, regardless of how good the
                  options look … a user forced to pick the nearest wrong one corrupts the binding." */}
              <div className="freetext">
                <label htmlFor={noteId}>
                  {gated && acknowledge.requiresReason
                    ? 'Why you are overriding this. Recorded verbatim, and printed in the report.'
                    : 'Anything the options did not anticipate, recorded verbatim'}
                </label>
                <input id={noteId} type="text" value={note}
                       onChange={(event) => setNote(event.target.value)} />
              </div>

              {gated && (
                <label className="opt">
                  <input type="checkbox" checked={acknowledged}
                         onChange={(event) => setAcknowledged(event.target.checked)} />
                  <span>{acknowledge.text}</span>
                </label>
              )}
            </fieldset>

            <div className="actions">
              <button type="button" className="btn primary"
                      disabled={!answerable || busy}
                      onClick={() => onAnswer({
                        // A READER WHO TYPED ONLY FREE TEXT HAS STILL ANSWERED. The decision is the
                        // empty string rather than a manufactured option, and the note carries what
                        // they meant — never the literal word "Other".
                        decision: chosen ?? '',
                        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
                      })}>
                {busy ? 'Sending…' : 'Submit'}
              </button>
              <span className="note" style={{ alignSelf: 'center' }}>
                {noteAnswers
                  ? 'Choose an option, or write one.'
                  : 'Choose an option. Anything you write is recorded with it.'}
              </span>
            </div>
          </>
        ) : (
          <div className="recorded">
            <span className="lab">Recorded</span>
            <span>
              <b>{labelOf(options, answer.decision)}</b>
              {answer.note !== null && answer.note.length > 0 && <>: “{answer.note}”</>}
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

/** The kind, as a heading. The ten are a closed vocabulary and are never truncated. */
function label(kind: string): string {
  const words = kind.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * `Answered`, with WHEN — and the date too, unless it was today.
 *
 * <p>This printed the clock time alone, so a decision taken ten days ago read `Answered 01:42 PM`
 * and looked like one taken at lunchtime. The instant was always complete; only the rendering
 * dropped the date. <see cref="formatWhen"/> carries the rule and the viewer's-calendar
 * comparison.</p>
 *
 * <p>An instant that is absent or unparseable still says `Answered`: the decision is the fact, and
 * a card that hid it because a timestamp was malformed would lose the more important half.</p>
 */
function answeredState(answer: { at: string | null }): string {
  const when = formatWhen(answer.at)
  return when === null ? 'Answered' : `Answered ${when}`
}

/**
 * The LABEL for a recorded verdict, or the verdict itself.
 *
 * <p>A decision this build has no option for is shown as it was recorded rather than hidden. The
 * harness may add a verdict; a card that rendered a blank where the decision should be would be
 * telling the reader nothing was decided.</p>
 */
function labelOf(options: readonly GateOption[], decision: string): string {
  if (decision.length === 0) return 'Answered in free text'
  return options.find((o) => o.value === decision)?.label ?? decision
}
